import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Grid, Link, Stack, TextField, Typography } from '@mui/material';
import { publicStorefrontApi, StorefrontApiError, StorefrontCheckoutResult, StorefrontPaymentConfig } from '../../services/storefrontService';
import { tokenService } from '../../services/tokenService';
import { useStorefront } from '../../components/Storefront/StorefrontContext';
import PayPalButtons from '../../components/Storefront/PayPalButtons';
import { formatMoney } from '../../components/Storefront/storefrontHelpers';
import { useStorefrontT } from '../../components/Storefront/useStorefrontT';
import { useStorefrontProducts } from './useStorefrontProducts';

/**
 * `/store/:slug/checkout` — Gast (E-Mail + Passwort, Konto wird im Mandanten angelegt)
 * oder angemeldet; Rechnungsadresse und Zustimmungen ab dem ersten Cent; danach
 * PayPal-Buttons (Summe > 0) bzw. sofortige Einschreibung (0 €).
 */
const StorefrontCheckout: React.FC = () => {
    const { t, locale } = useStorefrontT();
    const navigate = useNavigate();
    const { slug, storefront, cart, clearCart } = useStorefront();
    const { products } = useStorefrontProducts(slug);
    const loggedIn = tokenService.hasValidToken();

    const items = useMemo(() => (products ?? []).filter((p) => cart.includes(p.id)), [products, cart]);
    const total = items.reduce((sum, p) => sum + Number(p.price?.amount ?? 0), 0);
    const currency = items[0]?.price?.currency ?? 'EUR';
    const paid = total > 0;

    const [form, setForm] = useState({ email: '', firstName: '', lastName: '', password: '', street: '', zip: '', city: '', country: 'DE', vatId: '' });
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [acceptImmediateAccess, setAcceptImmediateAccess] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pending, setPending] = useState<{ result: StorefrontCheckoutResult; config: StorefrontPaymentConfig } | null>(null);

    const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const guestBlocked = !loggedIn && !storefront.allowGuestCheckout;
    const canSubmit = items.length > 0 && acceptTerms && (!paid || acceptImmediateAccess) && !busy && !pending && !guestBlocked;

    const goToOrder = (orderId: string, email: string) => {
        clearCart();
        navigate(`/store/${slug}/orders/${orderId}?email=${encodeURIComponent(email)}`);
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            const result = await publicStorefrontApi.checkout(slug, {
                productIds: items.map((p) => p.id),
                acceptTerms,
                acceptImmediateAccess,
                vatId: form.vatId || undefined,
                ...(loggedIn ? {} : { email: form.email, firstName: form.firstName, lastName: form.lastName, password: form.password }),
                ...(paid
                    ? { billingAddress: { name: `${form.firstName} ${form.lastName}`.trim() || undefined, street: form.street, zip: form.zip, city: form.city, country: form.country } }
                    : {}),
            });
            if (result.status === 'completed') {
                goToOrder(result.orderId, result.customerEmail);
                return;
            }
            const config = await publicStorefrontApi.paymentConfig(slug);
            setPending({ result, config });
        } catch (err) {
            setError(err instanceof StorefrontApiError ? err.message : t('checkout.errorGeneric'));
        } finally {
            setBusy(false);
        }
    };

    const capture = async (paypalOrderId: string) => {
        if (!pending) return;
        try {
            await publicStorefrontApi.capture(slug, pending.result.orderId, paypalOrderId, pending.result.customerEmail);
        } catch (err) {
            setError(err instanceof StorefrontApiError ? err.message : t('checkout.paymentFailed'));
            return;
        }
        goToOrder(pending.result.orderId, pending.result.customerEmail);
    };

    if (!products) return <CircularProgress />;

    return (
        <Box component="form" onSubmit={submit} noValidate>
            <Typography variant="h4" component="h1" gutterBottom>{t('checkout.title')}</Typography>
            {items.length === 0 ? <Alert severity="info">{t('cart.empty')}</Alert> : null}
            {guestBlocked ? <Alert severity="info" sx={{ mb: 2 }}>{t('checkout.guestDisabled')}</Alert> : null}
            {error ? <Alert severity="error" sx={{ mb: 2 }} role="alert">{error}</Alert> : null}

            <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 7 }}>
                    <Typography variant="h6" component="h2" gutterBottom>{t('checkout.account')}</Typography>
                    {loggedIn ? (
                        <Alert severity="success" sx={{ mb: 2 }}>{t('checkout.loggedIn')}</Alert>
                    ) : (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth required label={t('checkout.firstName')} value={form.firstName} onChange={set('firstName')} autoComplete="given-name" /></Grid>
                            <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={t('checkout.lastName')} value={form.lastName} onChange={set('lastName')} autoComplete="family-name" /></Grid>
                            <Grid size={12}><TextField fullWidth required type="email" label={t('checkout.email')} value={form.email} onChange={set('email')} autoComplete="email" /></Grid>
                            <Grid size={12}><TextField fullWidth required type="password" label={t('checkout.password')} helperText={t('checkout.passwordHelp')} value={form.password} onChange={set('password')} autoComplete="new-password" /></Grid>
                        </Grid>
                    )}

                    {paid ? (
                        <>
                            <Typography variant="h6" component="h2" gutterBottom>{t('checkout.billing')}</Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid size={12}><TextField fullWidth required label={t('checkout.street')} value={form.street} onChange={set('street')} autoComplete="street-address" /></Grid>
                                <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth required label={t('checkout.zip')} value={form.zip} onChange={set('zip')} autoComplete="postal-code" /></Grid>
                                <Grid size={{ xs: 12, sm: 8 }}><TextField fullWidth required label={t('checkout.city')} value={form.city} onChange={set('city')} autoComplete="address-level2" /></Grid>
                                <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth required label={t('checkout.country')} value={form.country} onChange={set('country')} slotProps={{ htmlInput: { maxLength: 2 } }} /></Grid>
                                <Grid size={{ xs: 12, sm: 8 }}><TextField fullWidth label={t('checkout.vatId')} value={form.vatId} onChange={set('vatId')} /></Grid>
                            </Grid>
                        </>
                    ) : null}

                    <FormControlLabel
                        control={<Checkbox checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />}
                        label={
                            <span>
                                {t('checkout.acceptTerms')}{' '}
                                {storefront.termsUrl ? <Link href={storefront.termsUrl} target="_blank" rel="noopener noreferrer">{t('footer.terms')}</Link> : null}
                            </span>
                        }
                    />
                    {paid ? (
                        <FormControlLabel control={<Checkbox checked={acceptImmediateAccess} onChange={(e) => setAcceptImmediateAccess(e.target.checked)} />} label={t('checkout.acceptImmediateAccess')} />
                    ) : null}
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                    <Box sx={{ p: 2, border: '1px solid var(--oa-divider, #dee2e6)', borderRadius: 1 }}>
                        <Typography variant="h6" component="h2" gutterBottom>{t('checkout.summary')}</Typography>
                        <Stack spacing={1} sx={{ mb: 2 }}>
                            {items.map((p) => (
                                <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                    <span>{p.title}</span>
                                    <span>{p.price && !p.price.isFree ? formatMoney(p.price.amount, p.price.currency, locale === 'en' ? 'en-GB' : 'de-DE') : t('product.free')}</span>
                                </Box>
                            ))}
                        </Stack>
                        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{t('cart.total')}: {paid ? formatMoney(total, currency, locale === 'en' ? 'en-GB' : 'de-DE') : t('product.free')}</Typography>
                        {paid ? <Typography variant="caption" color="text.secondary">{t('product.vatIncluded')}</Typography> : null}

                        <Box sx={{ mt: 3 }}>
                            {pending ? (
                                <>
                                    <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('checkout.payPalTitle')}</Typography>
                                    {pending.config.sellerCanReceivePayments && pending.config.configured ? (
                                        <PayPalButtons
                                            config={pending.config}
                                            paypalOrderId={pending.result.paypalOrderId ?? ''}
                                            onApprove={capture}
                                            onError={() => setError(t('checkout.paymentFailed'))}
                                            loadingLabel={t('checkout.payPalLoading')}
                                            unavailableLabel={t('checkout.payPalUnavailable')}
                                        />
                                    ) : (
                                        <Alert severity="warning">{t('checkout.payPalUnavailable')}</Alert>
                                    )}
                                </>
                            ) : (
                                <Button type="submit" variant="contained" fullWidth size="large" disabled={!canSubmit}>
                                    {busy ? t('checkout.processing') : paid ? t('checkout.submit') : t('checkout.submitFree')}
                                </Button>
                            )}
                        </Box>
                        {guestBlocked ? <Button component={RouterLink} to="/" sx={{ mt: 1 }}>{t('checkout.login')}</Button> : null}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default StorefrontCheckout;
